import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EmailFaxRequest } from 'src/app/core/classes/email-fax-request';
import { EmailFaxShare } from 'src/app/core/classes/email-fax-response';
import { OrderResult, ReportDTO, TestResultGraphResponse } from 'src/app/core/classes/orderResult';
import { OrderResultRequest } from 'src/app/core/classes/orderResultRequest';
import { Patient } from 'src/app/core/classes/patient';
import { PatientRequest } from 'src/app/core/classes/patient-request';
import { ReportInfo } from 'src/app/core/classes/reportInfo';
import { FieldConfig } from 'src/app/core/interfaces/field-config';
import { LinkedAccountResultDto } from 'src/app/core/interfaces/linked-account-result';
import { environment } from 'src/environments/environment';
import { HttpService } from 'src/app/core/services/http/http.service';
import { Constants } from 'src/app/shared/constants';
import { EndPoints } from 'src/app/shared/EndPoints';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { HttpResponse } from '@angular/common/http';
import { TestResultGraphRequest } from 'src/app/core/classes/test-result-graph-request';

@Injectable({
  providedIn: 'root'
})
export class ResultsService {

  private CPPortalBaseUrl: string;
  public searchTerm: string;
  public filterType: string;
  private cdnImageUrl: string;
  public accountCodes = new Array<string>();
  public payloadData: any;
  public reportLogo: string;
  public resultCount: number;
  constructor(
    private httpService: HttpService,
    private localStorageService: LocalStorageService) {
    this.CPPortalBaseUrl = environment.customerPortalServiceUrl;
    this.cdnImageUrl = environment.cdnImageServiceUrl;
    this.reportLogo = environment.report_logo;
  }

  public SetReportData(report: ReportInfo) {
    this.localStorageService.setItem(Constants.REPORT_INFO, JSON.stringify(report));
  }

  public GetReportData() {
    return JSON.parse(this.localStorageService.getItem(Constants.REPORT_INFO));
  }

  public getCDNImageUrl() {
    return this.cdnImageUrl;
  }

  public async getEasyOrderReport(request: string) {
    return await this.httpService.AsyncGet(this.CPPortalBaseUrl
      + EndPoints.GET_ORDER_BY_REQUISITION_NUMBER
      + request);
  }

  public resetNavigationFilter() {
    this.searchTerm = null;
    this.filterType = null;
    this.payloadData = null;
  }

  public getResults(request: OrderResultRequest): Observable<OrderResult[]> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getResults, request);
  }

  public getTestDetailsForGraph(request: TestResultGraphRequest): Observable<TestResultGraphResponse[]>{
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getTestDetailsForGraph, request);
  }

  public getLinkedAccounts(userId: string): Observable<LinkedAccountResultDto[]> {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.GET_LINKED_ACCOUNTS_BY_ID);
  }

  public getUserLinkedAccounts(): Observable<LinkedAccountResultDto[]> {
    return this.httpService.Get(this.CPPortalBaseUrl +
           EndPoints.GET_USER_LINKED_ACCOUNTS );
  }
  public getPatients(request: PatientRequest): Observable<Patient[]> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.GET_PATIENT_BY_NAME, request);
  }

  public reportDownload(request: ReportDTO) {
    const requestOptions: object = {
      responseType: HttpResponse
    };
    return this.httpService.PostWithOption(this.CPPortalBaseUrl + EndPoints.reportDownload, request, requestOptions);
  }
  public downloadReportAndUpdateIsRead(request: any) {
    const requestOptions: object = {
      responseType: HttpResponse
    };
    return this.httpService
    .PostWithOption(this.CPPortalBaseUrl + EndPoints.DownloadReportAndUpdateIsRead, request, requestOptions);
  }

  public async reportSend(request: EmailFaxRequest) {
    return await this.httpService.AsyncPost(this.CPPortalBaseUrl + EndPoints.reportSend, request);
  }

  public async reportSendFax(request: EmailFaxRequest) {
    return await this.httpService.AsyncPost(this.CPPortalBaseUrl + EndPoints.reportSendFax, request);
  }

  public exportOrderResults(request: OrderResultRequest): Observable<OrderResult[]> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.exportOrderResults, request);
  }

  public downloadOrderResults(request: OrderResultRequest) {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.downloadOrderResults, request);
  }

  public getEmailsFaxNumbers(accounts: string[]): Observable<EmailFaxShare> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.emailFaxShare, accounts);
  }

  public getMainAccountsDTO(accounts: string[]): Observable<any> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.GET_MAIN_ACCOUNT_DTO, accounts);
  }

  public acknowledgeResults(AccessionId: string, data: string) {
    const postData = {
      accessionId: AccessionId,
      signedBy: data
    };
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.acknowledge, postData);
  }

  public async getDetailedResults(AccessionId, BuCode) {
    const postData = {
      AccessionID: AccessionId,
      BU: BuCode
    };
    return (await this.httpService.AsyncPost(this.CPPortalBaseUrl +
      EndPoints.getDetailedResults, postData));
  }

  public getSonographerDropdown(accountCode: string): Observable<any> {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.getSonographerList + accountCode);
  }

  public getDemographicConfig(testcode: string): Observable<FieldConfig[]> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getTestUIControls + '/' + testcode, null);
  }

  public getDemographicData(AccessionId, BuCode): Observable<any> {
    const postData = {
      accessionID: AccessionId,
      bu: BuCode
    };
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getDemographicData, postData);
  }

  public updateDemographicData(demographicdata: any): Observable<any> {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.updateDemographic, demographicdata);
  }

  public async getReportData(AccessionId, BuCode) {
    const postData = {
      AccessionID: AccessionId,
      BU: BuCode
    };
    return (await this.httpService.AsyncPost(this.CPPortalBaseUrl +
      EndPoints.GetReportData, postData));
  }

  public generateNewReport(AccessionId, BuCode): Observable<any> {
    const postData = {
      AccessionID: AccessionId,
      BU: BuCode
    };
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.GenerateNewReport, postData);
  }

  public getComputationFormulas(): Observable<any> {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.getComputationFormula);
  }

  public getLinkedAccountsByBU(buCode: string) {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.getLinkedAccountsByBUCode + buCode);
  }

  public getLinkedAccountsAsPerBU(buCode: string) {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.getLinkedAccountsAsPerBUCode + buCode);
  }

  public getEventsByLoggedInUserInfo(accountCodes: string[]) {
    const request = {
      accountCodes,
      includeInactiveEvents: true
    };
    return this.httpService.Post(this.CPPortalBaseUrl +
      EndPoints.GetEventsByLoggedinUserInfo, request);
  }

  public getMultiplePdfsMergedReport(names: string[]) {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.mergeMultipleReportsAndDownload, names);
  }

  public getIfAllReportsExists(reportDtos: ReportDTO[]) {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getIfAllReportsExists, reportDtos);
  }

  public getBusinessUnitDetailsForResults(): any {
    return this.httpService.Get(this.CPPortalBaseUrl + EndPoints.GET_BUSINESSUNIT_DETAILS_FOR_RESULTS);
  }

  public getClientId1SearchResults(request: any) {
    return this.httpService.Post(this.CPPortalBaseUrl + EndPoints.getClientId1SearchResults, request);
  }

}
